import { z } from 'zod';

import { MAX_DAY_OFFSET, MIN_DAY_OFFSET } from '../deactivationConfig';

import {
  mappingEntryObjectSchema,
  optionalMappingEntrySchema,
  requiredMappingEntryObjectSchema,
  valueMappingCodec,
} from './common';

/**
 * Nested object schema for address
 */
const addressSchema = z
  .object({
    city: optionalMappingEntrySchema,
    houseNumber: optionalMappingEntrySchema,
    postCode: optionalMappingEntrySchema,
    streetName: optionalMappingEntrySchema,
  })
  .optional();

/**
 * Nested object schema for coordinates
 */
const coordsSchema = z
  .object({
    latitude: optionalMappingEntrySchema,
    longitude: optionalMappingEntrySchema,
  })
  .optional();

/**
 * Site entity data mapping schema
 */
export const siteDataMappingSchema = z.object({
  accRefSyncKeys: z.array(mappingEntryObjectSchema).optional(),
  address: addressSchema,
  companyId: optionalMappingEntrySchema,
  companySyncKey: optionalMappingEntrySchema,
  coords: coordsSchema,
  end: optionalMappingEntrySchema,
  isActive: optionalMappingEntrySchema,
  // The backend forces isActive to false wherever this maps to true.
  isDeleted: optionalMappingEntrySchema,
  name: requiredMappingEntryObjectSchema,
  start: optionalMappingEntrySchema,
  syncKey: optionalMappingEntrySchema,
  type: optionalMappingEntrySchema,
});

/**
 * User entity data mapping schema
 */
export const userDataMappingSchema = z.object({
  companyId: optionalMappingEntrySchema,
  companySyncKey: optionalMappingEntrySchema,
  email: requiredMappingEntryObjectSchema,
  firstName: requiredMappingEntryObjectSchema,
  isActive: optionalMappingEntrySchema,
  lastName: requiredMappingEntryObjectSchema,
  permittedSignatureTypes: optionalMappingEntrySchema,
  position: optionalMappingEntrySchema,
  syncKey: optionalMappingEntrySchema,
  userType: optionalMappingEntrySchema,
});

/**
 * Organizational unit entity data mapping schema
 */
export const organizationalUnitDataMappingSchema = z.object({
  name: requiredMappingEntryObjectSchema,
});

/**
 * Permission grant entity data mapping schema
 */
export const permissionGrantDataMappingSchema = z.object({
  role: requiredMappingEntryObjectSchema,
  subjectSyncKey: requiredMappingEntryObjectSchema,
  subjectType: requiredMappingEntryObjectSchema,
  targetSyncKey: requiredMappingEntryObjectSchema,
  targetType: requiredMappingEntryObjectSchema,
});

/**
 * Accounting reference entity data mapping schema
 */
export const accountingReferenceDataMappingSchema = z.object({
  companyId: optionalMappingEntrySchema,
  companySyncKey: optionalMappingEntrySchema,
  end: optionalMappingEntrySchema,
  isActive: optionalMappingEntrySchema,
  name: requiredMappingEntryObjectSchema,
  start: optionalMappingEntrySchema,
  syncKey: optionalMappingEntrySchema,
  type: optionalMappingEntrySchema,
});

/**
 * Map of entity types to their dataMapping schemas
 */
export const ENTITY_DATA_MAPPING_SCHEMAS = {
  accounting_reference: accountingReferenceDataMappingSchema,
  organizational_unit: organizationalUnitDataMappingSchema,
  permission_grant: permissionGrantDataMappingSchema,
  site: siteDataMappingSchema,
  user: userDataMappingSchema,
} as const;

/**
 * Shared schema for a deactivation day-offset
 */
const dayOffsetSchema = z
  .number()
  .int()
  .min(MIN_DAY_OFFSET)
  .max(MAX_DAY_OFFSET)
  .nullable()
  .optional();

/**
 * Base schema shared across all entity types
 */
const baseTaskSchema = z.object({
  dataSyncJobId: z.uuid(),
  // Per-task auto-deactivation rules. Only applied by the backend for `site`
  // and `accounting_reference` entities (no-op otherwise, and stripped from the
  // payload for those types, see omitUnsupportedDeactivationConfig). Omitted
  // entirely when the user never touches it, so the backend keeps its default
  // behavior. Inner keys are optional to mirror the backend model (every field
  // has a default): the UI sends only the fields the user actually set, a
  // cleared field is sent as explicit null (rule off), and each day-offset is
  // an integer within the shared bounds.
  deactivationConfig: z
    .object({
      daysAfterEnd: dayOffsetSchema,
      daysAfterStart: dayOffsetSchema,
      daysBeforeStart: dayOffsetSchema,
      enabled: z.boolean().optional(),
    })
    .optional()
    .nullable(),
  name: z.string().min(1).nullable(),
  valueMapping: valueMappingCodec.optional().nullable(),
});

/**
 * Discriminated union based on entityType with entity-specific dataMapping schemas
 * Codecs handle bidirectional transformations automatically
 */
export const dataMappingSchema = z.discriminatedUnion('entityType', [
  baseTaskSchema.extend({
    dataMapping: siteDataMappingSchema,
    entityType: z.literal('site'),
  }),
  baseTaskSchema.extend({
    dataMapping: userDataMappingSchema,
    entityType: z.literal('user'),
  }),
  baseTaskSchema.extend({
    dataMapping: organizationalUnitDataMappingSchema,
    entityType: z.literal('organizational_unit'),
  }),
  baseTaskSchema.extend({
    dataMapping: permissionGrantDataMappingSchema,
    entityType: z.literal('permission_grant'),
  }),
  baseTaskSchema.extend({
    dataMapping: accountingReferenceDataMappingSchema,
    entityType: z.literal('accounting_reference'),
  }),
]);
